/**
 * XSS Protection utilities for comprehensive security
 */
import DOMPurify from 'dompurify';
import { logger } from './secureLogger';

// Enhanced XSS protection configuration
const SECURITY_CONFIG = {
  STRICT: {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: [],
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button', 'iframe', 'link', 'style'],
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'style', 'javascript:']
  },
  STANDARD: {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'blockquote', 'div', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button', 'iframe'],
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'javascript:']
  }
};

/**
 * Sanitize HTML content with enhanced security
 */
export const sanitizeHtml = (content: string, level: 'STRICT' | 'STANDARD' = 'STANDARD'): string => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  const config = SECURITY_CONFIG[level];
  
  try {
    const sanitized = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: config.ALLOWED_TAGS,
      ALLOWED_ATTR: config.ALLOWED_ATTR,
      FORBID_TAGS: config.FORBID_TAGS,
      FORBID_ATTR: config.FORBID_ATTR,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_TRUSTED_TYPE: false
    });

    // Log potential XSS attempts
    if (content !== sanitized) {
      logger.securityEvent('XSS_CONTENT_SANITIZED', 'system', {
        originalLength: content.length,
        sanitizedLength: sanitized.length,
        removedContent: content.length - sanitized.length
      });
    }

    return sanitized;
  } catch (error) {
    logger.error('XSS sanitization failed', { error: error instanceof Error ? error.message : 'Unknown error' });
    return ''; // Return empty string on sanitization failure
  }
};

/**
 * Validate and sanitize user input for text fields
 */
export const sanitizeTextInput = (input: string): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove potentially dangerous patterns
  return input
    .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remove script tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .replace(/data:text\/html/gi, '') // Remove data URIs
    .trim();
};

/**
 * Check if content contains potential XSS patterns
 */
export const detectXssPatterns = (content: string): boolean => {
  if (!content || typeof content !== 'string') {
    return false;
  }

  const xssPatterns = [
    /<script[^>]*>/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe[^>]*>/i,
    /<object[^>]*>/i,
    /<embed[^>]*>/i,
    /data:text\/html/i,
    /<link[^>]*>/i,
    /<style[^>]*>/i
  ];

  return xssPatterns.some(pattern => pattern.test(content));
};

/**
 * Comprehensive XSS protection for form data
 */
export const sanitizeFormData = (data: Record<string, any>): Record<string, any> => {
  const sanitized: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      // Check for XSS patterns first
      if (detectXssPatterns(value)) {
        logger.securityEvent('XSS_ATTEMPT_DETECTED', 'system', {
          field: key,
          suspiciousContent: value.substring(0, 100) // Log first 100 chars only
        });
      }
      
      sanitized[key] = sanitizeTextInput(value);
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeFormData(value); // Recursive sanitization
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Safe HTML renderer for React components
 */
export const createSafeHtmlProps = (content: string, level: 'STRICT' | 'STANDARD' = 'STANDARD') => {
  const sanitizedContent = sanitizeHtml(content, level);
  
  return {
    dangerouslySetInnerHTML: { __html: sanitizedContent }
  };
};

/**
 * Runtime XSS protection monitor
 */
export const initializeXssMonitor = () => {
  // Monitor for dangerous DOM modifications
  if (typeof window !== 'undefined' && window.MutationObserver) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              
              // Check for script tags
              if (element.tagName?.toLowerCase() === 'script') {
                logger.securityEvent('SUSPICIOUS_SCRIPT_INJECTION', 'system', {
                  tagName: element.tagName,
                  innerHTML: element.innerHTML.substring(0, 100)
                });
              }
              
              // Check for inline event handlers
              Array.from(element.attributes || []).forEach((attr) => {
                if (attr.name.startsWith('on')) {
                  logger.securityEvent('SUSPICIOUS_EVENT_HANDLER', 'system', {
                    attributeName: attr.name,
                    attributeValue: attr.value.substring(0, 100)
                  });
                }
              });
            }
          });
        }
      });
    });

    // Start monitoring
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur']
    });

    logger.info('XSS monitoring initialized');
  }
};